// ===========================================
// Business Card Import Session Hook
// ===========================================
// Manages batch import state: uploading, AI parsing,
// duplicate detection, review, and import.

import { useState, useCallback, useRef } from 'react';
import { parseBusinessCard, type BusinessCardData } from '@/services/businessCardRecognition';
import { createCompany, createContact } from '@/services/companies';
import { normalizeName, diceSimilarity, SIMILARITY_THRESHOLD } from '@/services/mergeCompanies';
import { useCompanies } from '@/hooks/useCompanies';
import type { Company, CreateCompanyInput, CreateContactInput } from '@/types';

// ===========================================
// Types
// ===========================================

export interface DuplicateMatch {
  companyId: string;
  companyName: string;
  matchType: 'exact' | 'similar';
  similarity: number;
}

export interface BusinessCardEntry {
  id: string;
  file: File;
  thumbnailUrl: string;
  status: 'pending' | 'processing' | 'parsed' | 'error' | 'approved' | 'skipped';
  parsedData: BusinessCardData | null;
  editedData: BusinessCardData | null;
  error: string | null;
  confidence: 'high' | 'medium' | 'low' | null;
  warnings: string[];
  duplicateMatch: DuplicateMatch | null;
  importAction: 'new_company' | 'add_contact_to_existing';
  existingCompanyId: string | null;
}

export interface ImportResult {
  companiesCreated: number;
  contactsAdded: number;
  skipped: number;
  errors: number;
}

// ===========================================
// Hook
// ===========================================

const MAX_CONCURRENT = 3;

export function useBusinessCardImport(companyTypes: string[]) {
  const [cards, setCards] = useState<BusinessCardEntry[]>([]);
  const [sessionSalesRepId, setSessionSalesRepId] = useState('');
  const [sessionSalesRepName, setSessionSalesRepName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const { data: companies } = useCompanies();
  const processingCount = useRef(0);

  // Generate unique ID
  const genId = () => `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Check for duplicate companies
  const checkDuplicates = useCallback(
    (companyName: string): DuplicateMatch | null => {
      if (!companies || !companyName) return null;

      const normalized = normalizeName(companyName);
      if (!normalized) return null;

      let bestMatch: DuplicateMatch | null = null;
      let bestScore = 0;

      for (const existing of companies) {
        const existingNorm = normalizeName(existing.name);

        // Exact normalized match
        if (normalized === existingNorm) {
          return {
            companyId: existing.id,
            companyName: existing.name,
            matchType: 'exact',
            similarity: 1,
          };
        }

        // Fuzzy match
        const score = diceSimilarity(normalized, existingNorm);
        if (score >= SIMILARITY_THRESHOLD && score > bestScore) {
          bestScore = score;
          bestMatch = {
            companyId: existing.id,
            companyName: existing.name,
            matchType: 'similar',
            similarity: score,
          };
        }

        // Contains match
        const shorter = normalized.length <= existingNorm.length ? normalized : existingNorm;
        const longer = normalized.length > existingNorm.length ? normalized : existingNorm;
        if (shorter.length >= 4 && longer.includes(shorter) && !bestMatch) {
          bestMatch = {
            companyId: existing.id,
            companyName: existing.name,
            matchType: 'similar',
            similarity: 0.7,
          };
        }
      }

      return bestMatch;
    },
    [companies],
  );

  // Process a single card
  const processCard = useCallback(
    async (cardId: string) => {
      const card = cards.find((c) => c.id === cardId);
      if (!card || card.status !== 'pending') return;

      setCards((prev) =>
        prev.map((c) => (c.id === cardId ? { ...c, status: 'processing' as const } : c)),
      );

      try {
        const result = await parseBusinessCard(card.file, companyTypes);

        const duplicateMatch = result.success ? checkDuplicates(result.data.companyName) : null;

        setCards((prev) =>
          prev.map((c) =>
            c.id === cardId
              ? {
                  ...c,
                  status: 'parsed' as const,
                  parsedData: result.data,
                  editedData: { ...result.data },
                  confidence: result.confidence,
                  warnings: result.warnings || [],
                  duplicateMatch,
                  importAction: duplicateMatch ? ('add_contact_to_existing' as const) : ('new_company' as const),
                  existingCompanyId: duplicateMatch?.companyId || null,
                  error: result.success ? null : 'Could not read business card',
                }
              : c,
          ),
        );
      } catch (err: any) {
        setCards((prev) =>
          prev.map((c) =>
            c.id === cardId
              ? { ...c, status: 'error' as const, error: err.message || 'Processing failed' }
              : c,
          ),
        );
      }
    },
    [cards, companyTypes, checkDuplicates],
  );

  // Add files and start processing
  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const newCards: BusinessCardEntry[] = Array.from(files).map((file) => ({
        id: genId(),
        file,
        thumbnailUrl: URL.createObjectURL(file),
        status: 'pending' as const,
        parsedData: null,
        editedData: null,
        error: null,
        confidence: null,
        warnings: [],
        duplicateMatch: null,
        importAction: 'new_company' as const,
        existingCompanyId: null,
      }));

      setCards((prev) => [...prev, ...newCards]);
      return newCards.map((c) => c.id);
    },
    [],
  );

  // Process all pending cards with concurrency limit
  const processAllCards = useCallback(async () => {
    setIsProcessing(true);

    const pending = cards.filter((c) => c.status === 'pending');
    const queue = [...pending];

    const processNext = async (): Promise<void> => {
      const card = queue.shift();
      if (!card) return;

      processingCount.current++;
      try {
        // Inline processing to avoid stale closure on `cards`
        setCards((prev) =>
          prev.map((c) => (c.id === card.id ? { ...c, status: 'processing' as const } : c)),
        );

        const result = await parseBusinessCard(card.file, companyTypes);
        const duplicateMatch = result.success ? checkDuplicates(result.data.companyName) : null;

        setCards((prev) =>
          prev.map((c) =>
            c.id === card.id
              ? {
                  ...c,
                  status: 'parsed' as const,
                  parsedData: result.data,
                  editedData: { ...result.data },
                  confidence: result.confidence,
                  warnings: result.warnings || [],
                  duplicateMatch,
                  importAction: duplicateMatch ? ('add_contact_to_existing' as const) : ('new_company' as const),
                  existingCompanyId: duplicateMatch?.companyId || null,
                  error: result.success ? null : 'Could not read business card',
                }
              : c,
          ),
        );
      } catch (err: any) {
        setCards((prev) =>
          prev.map((c) =>
            c.id === card.id
              ? { ...c, status: 'error' as const, error: err.message || 'Processing failed' }
              : c,
          ),
        );
      } finally {
        processingCount.current--;
      }

      await processNext();
    };

    // Start up to MAX_CONCURRENT workers
    const workers = Array.from({ length: Math.min(MAX_CONCURRENT, queue.length) }, () =>
      processNext(),
    );
    await Promise.all(workers);

    setIsProcessing(false);
  }, [cards, companyTypes, checkDuplicates]);

  // Update a field on an edited card
  const updateCardData = useCallback(
    (cardId: string, field: keyof BusinessCardData, value: string) => {
      setCards((prev) =>
        prev.map((c) => {
          if (c.id !== cardId || !c.editedData) return c;
          const updated = { ...c, editedData: { ...c.editedData, [field]: value } };

          // Re-check duplicates if company name changed
          if (field === 'companyName') {
            const dup = checkDuplicates(value);
            updated.duplicateMatch = dup;
            if (!dup) {
              updated.importAction = 'new_company';
              updated.existingCompanyId = null;
            }
          }

          return updated;
        }),
      );
    },
    [checkDuplicates],
  );

  // Approve a card
  const approveCard = useCallback((cardId: string) => {
    setCards((prev) =>
      prev.map((c) => (c.id === cardId ? { ...c, status: 'approved' as const } : c)),
    );
  }, []);

  // Skip a card
  const skipCard = useCallback((cardId: string) => {
    setCards((prev) =>
      prev.map((c) => (c.id === cardId ? { ...c, status: 'skipped' as const } : c)),
    );
  }, []);

  // Approve all parsed cards
  const approveAll = useCallback(() => {
    setCards((prev) =>
      prev.map((c) => (c.status === 'parsed' ? { ...c, status: 'approved' as const } : c)),
    );
  }, []);

  // Set import action for a card
  const setImportAction = useCallback(
    (cardId: string, action: 'new_company' | 'add_contact_to_existing', existingCompanyId?: string) => {
      setCards((prev) =>
        prev.map((c) =>
          c.id === cardId
            ? { ...c, importAction: action, existingCompanyId: existingCompanyId || null }
            : c,
        ),
      );
    },
    [],
  );

  // Import all approved cards
  const importApprovedCards = useCallback(
    async (userId: string) => {
      setIsImporting(true);
      const result: ImportResult = { companiesCreated: 0, contactsAdded: 0, skipped: 0, errors: 0 };

      const approved = cards.filter((c) => c.status === 'approved');

      for (const card of approved) {
        const data = card.editedData || card.parsedData;
        if (!data) {
          result.errors++;
          continue;
        }

        try {
          if (card.importAction === 'new_company') {
            // Create new company
            const companyInput: CreateCompanyInput = {
              name: data.companyName,
              type: data.companyType || undefined,
              address: data.address || undefined,
              city: data.city || undefined,
              state: data.state || undefined,
              zip: data.zip || undefined,
              phone: data.phone || undefined,
              email: data.email || undefined,
              website: data.website || undefined,
              businessVisibility: 'both',
              salesRepUserId: sessionSalesRepId || undefined,
              salesRepName: sessionSalesRepName || undefined,
              // No commission by default
            };

            const created = await createCompany(companyInput, userId);
            result.companiesCreated++;

            // Create primary contact
            if (data.contactFirstName || data.contactLastName) {
              const contactInput: CreateContactInput = {
                firstName: data.contactFirstName || '',
                lastName: data.contactLastName || '',
                title: data.contactTitle || undefined,
                email: data.contactEmail || data.email || undefined,
                phone: data.contactPhone || data.phone || undefined,
                isPrimary: true,
              };
              await createContact(created.id, contactInput, userId);
              result.contactsAdded++;
            }
          } else if (card.importAction === 'add_contact_to_existing' && card.existingCompanyId) {
            // Add contact to existing company
            const contactInput: CreateContactInput = {
              firstName: data.contactFirstName || '',
              lastName: data.contactLastName || '',
              title: data.contactTitle || undefined,
              email: data.contactEmail || data.email || undefined,
              phone: data.contactPhone || data.phone || undefined,
              isPrimary: false, // Existing company already has contacts
            };
            await createContact(card.existingCompanyId, contactInput, userId);
            result.contactsAdded++;
          }
        } catch (err: any) {
          console.error(`[BusinessCardImport] Failed to import card ${card.id}:`, err);
          result.errors++;
        }
      }

      result.skipped = cards.filter((c) => c.status === 'skipped').length;
      setImportResult(result);
      setIsImporting(false);
      return result;
    },
    [cards, sessionSalesRepId, sessionSalesRepName],
  );

  // Reset session
  const resetSession = useCallback(() => {
    // Clean up object URLs
    cards.forEach((c) => URL.revokeObjectURL(c.thumbnailUrl));
    setCards([]);
    setImportResult(null);
  }, [cards]);

  return {
    cards,
    sessionSalesRepId,
    sessionSalesRepName,
    setSessionSalesRepId,
    setSessionSalesRepName,
    isProcessing,
    isImporting,
    importResult,
    addFiles,
    processAllCards,
    processCard,
    updateCardData,
    approveCard,
    skipCard,
    approveAll,
    setImportAction,
    importApprovedCards,
    resetSession,
  };
}
